import * as React from 'react';
import { useEffect, useState, useReducer, useCallback, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { ThemeProvider } from '@mui/material/styles';
import {
  Card,
  Container,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  TableFooter,
  Box,
  InputAdornment,
  TableCell,
  TablePagination,
  Stack,
  DialogContent,
  DialogTitle,
  Dialog,
  Grid,
  Tooltip,
  DialogActions,
  MenuItem,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
//--------------------------------------------------------
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
import Lock from '@mui/icons-material/Https';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PublicIcon from '@mui/icons-material/Public';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';

//--------------------------------------------------------
import { CustomTextField } from '../../../utils/StyledInputs';
import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton } from '../../../utils/Buttons';
import { ErrorUI, HttpErrorHandler } from '../../../utils/ErrorHandler';
import { PaginationAPI, theme } from '../../../utils/TableStyles';
import { LoadingUI } from '../../../utils/LayoutStyles';
import { CommonLabel, MembershipCardIcon } from '../../../utils/Common';
import { EntitiesDialog } from '../../../utils/EntitiesDialog';
import API from '../../../api';
import NewUserDialog from './components/NewUserDialog';

dayjs.locale('it');

export default function Users() {
  // genaral
  const navigate = useNavigate();
  const is400Screen = useMediaQuery('(min-width: 400px)');
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [currentProfile, setCurrentProfile] = useState('');
  const [canCreateUser, setCanCreateUser] = useState(false);

  // ---------------------------- User details section ---------------------------------

  // api static data
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  // api handler
  const [apiResponseData, setApiResponseData] = useState({});
  const [apiCalled, setApiCalled] = useState(false);
  const [rowsApiCalled, setRowsApiCalled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Search mode: 'manual' or 'filter'
  const [searchMode, setSearchMode] = useState('manual');
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [filterSearchText, setFilterSearchText] = useState('');
  const [filtersLoading, setFiltersLoading] = useState(false);
  const debounceTimerRef = useRef(null);

  // Debounced filter search - triggers after 3+ chars with 500ms delay
  const searchFilters = useCallback((searchText) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (searchText.length < 3) {
      setSavedFilters([]);
      return;
    }

    setFiltersLoading(true);
    debounceTimerRef.current = setTimeout(() => {
      API.get('/filters', { params: { search: searchText } })
        .then((response) => {
          setSavedFilters(response.data || []);
        })
        .catch(() => {
          setSavedFilters([]);
        })
        .finally(() => {
          setFiltersLoading(false);
        });
    }, 500);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchModeChange = (newMode) => {
    if (newMode === searchMode) return;
    setSearchMode(newMode);
    setSelectedFilter(null);
    setFilterSearchText('');
    setSavedFilters([]);
    dispatchFormState({ type: 'RESET_FILTERS' });
    setPage(0);
    sessionStorage.setItem('page', JSON.stringify(0));
    sessionStorage.removeItem('userFilters');
  };

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
    // setOpen(false);
  };

  // new user dialog handler
  const [openNewUser, setOpenNewUser] = useState(false);
  const [newUserMode, setNewUserMode] = useState(null);

  // open new user dialog
  const handleOpenNewUser = () => {
    setOpenNewUser(true);
  };

  const [canVerifyUsers, setCanVerifyUsers] = useState(false);

  // get initial apis
  useEffect(() => {
    const savedFilters = sessionStorage.getItem('userFilters');
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      Object.keys(filters).forEach((key) => {
        if (key === 'entity') {
          handleEntityChange(filters[key]);
        } else {
          handleFieldChange(key, filters[key]);
        }
      });
    }
    getUsers();

    const data = JSON.parse(localStorage.getItem('userData'));
    setCanVerifyUsers(data.can_verify_users);
  }, []);

  /* -------------------------------- Get Users ------------------------------- */

  const [formState, dispatchFormState] = useReducer(formReducer, {
    name: '',
    surname: '',
    email: '',
    phone: '',
    card_number: '',
    fiscal_code: '',
    entity: null,
    category: '',
  });

  const saveFiltersToSessionStorage = (state) => {
    sessionStorage.setItem('userFilters', JSON.stringify(state));
  };

  function formReducer(state, action) {
    switch (action.type) {
      case 'CHANGE_FIELD':
        return { ...state, [action.field]: action.value };
      case 'CHANGE_ENTITY':
        return { ...state, entity: action.value };
      case 'RESET_FILTERS':
        return {
          name: '',
          surname: '',
          email: '',
          phone: '',
          card_number: '',
          fiscal_code: '',
          entity: null,
          category: '',
        };
      default:
        return state;
    }
  }

  const handleFieldChange = (field, value) => {
    dispatchFormState({ type: 'CHANGE_FIELD', field, value });
  };

  const handleEntityChange = (value) => {
    dispatchFormState({ type: 'CHANGE_ENTITY', value });
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (searchMode === 'manual') {
      saveFiltersToSessionStorage(formState);
    }
    setPage(0);
    sessionStorage.setItem('page', JSON.stringify(0));

    getUsers();
  };

  const handleResetFilters = (e) => {
    e.preventDefault();

    dispatchFormState({ type: 'RESET_FILTERS' });
    sessionStorage.setItem(
      'userFilters',
      JSON.stringify({
        name: '',
        surname: '',
        email: '',
        phone: '',
        card_number: '',
        fiscal_code: '',
        entity: null,
        category: '',
      })
    );
    sessionStorage.setItem('page', JSON.stringify(0));
    setPage(0);

    getUsers();
  };

  // get all users
  const getUsers = (overrideFilterId) => {
    const savedPage = Number(sessionStorage.getItem('page'));
    const activeFilterId = overrideFilterId !== undefined ? overrideFilterId : selectedFilter ? selectedFilter.id : '';

    let queryString = '';

    if (searchMode === 'filter' && activeFilterId) {
      queryString = `filter_id=${activeFilterId}`;
    } else if (searchMode === 'manual') {
      let filters = formState;
      const savedFiltersStr = sessionStorage.getItem('userFilters');
      if (savedFiltersStr) {
        filters = JSON.parse(savedFiltersStr);
      }

      queryString = Object.keys(filters)
        .filter((key) => (key === 'entity' ? filters[key] !== null : filters[key] && filters[key].length > 0))
        .map((key) => {
          if (key === 'entity') {
            return `entity_id=${filters[key].id}`;
          }
          return `${key}=${filters[key]}`;
        })
        .join('&');
    }

    API.get(`/users?${queryString}&page=${savedPage + 1}`)
      .then((response) => {
        setApiResponseData(response.data);
        setTotalUsers(response.data.total);
        setCanCreateUser(response.data.auth_type === 'W' || response.data.auth_type === 'D');
        setUsers(response.data.data);
        setApiCalled(true);
        setRowsApiCalled(true);
      })
      .catch((error) => {
        setApiCalled(true);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  useEffect(() => {
    setUsers(apiResponseData.data);
  }, [apiResponseData]);

  const [userToToggleVerifiedInfo, setUserToToggleVerifiedInfo] = useState({
    user: {},
    isVerified: null,
  });
  const [openToggleVerified, setOpenToggleVerified] = useState(false);

  const openToggleVerifiedDialog = (user, isVerified) => {
    setOpenToggleVerified(true);
    setUserToToggleVerifiedInfo({
      user,
      isVerified,
    });
  };

  const closeToggleVerifiedDialog = () => {
    setOpenToggleVerified(false);
    setUserToToggleVerifiedInfo({
      user: {},
      isVerified: null,
    });
  };

  const handleToggleVerified = () => {
    const url = `/users/${userToToggleVerifiedInfo.user.id}/verified?val=${
      userToToggleVerifiedInfo.isVerified ? 0 : 1
    }`;

    API.patch(url).then((response) => {
      closeToggleVerifiedDialog();

      getUsers();
    });
  };

  /* ------------------------------- Pagination ------------------------------- */
  const savedPage = sessionStorage.getItem('page') || 0;
  const [page, setPage] = useState(Number(savedPage));
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const [sliceStartDisplay, setSliceStartDisplay] = useState(0);
  const [sliceEndDisplay, setSliceEndDisplay] = useState(0);

  // empty rows number
  const emptyRows = users?.length
    ? page > 0
      ? rowsPerPage === 5 && page % 2 === 0
        ? Math.max(0, 1 * rowsPerPage - users.length)
        : rowsPerPage === 5 && page % 2 !== 0
        ? Math.max(0, 1 * rowsPerPage + 5 - users.length)
        : rowsPerPage === 10
        ? Math.max(0, 1 * rowsPerPage - users.length)
        : 0
      : 0
    : 0;

  // dynamically displays users number
  useEffect(() => {
    if (rowsPerPage === 10) {
      setSliceStartDisplay(0);
      setSliceEndDisplay(10);
    } else if (rowsPerPage === 5) {
      if (page % 2 === 0) {
        setSliceStartDisplay(0);
        setSliceEndDisplay(5);
      } else {
        setSliceStartDisplay(5);
        setSliceEndDisplay(10);
      }
    }
  }, [rowsPerPage, page]);

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem('page', JSON.stringify(newPage));

    // getUsers(newPage)
  };

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    sessionStorage.setItem('page', JSON.stringify(0));
    getUsers();
    setRowsApiCalled(false);
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  return (
    <>
      <Helmet>
        <title>Anagrafiche Utenti</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          sx={{ paddingBottom: '24px' }}
        >
          {apiCalled && errorMessage?.length === 0 && (
            <DefaultContainedButton
              onClick={() => {
                setNewUserMode('contact');
                handleOpenNewUser();
              }}
              customStyles={{ display: canCreateUser ? 'flex' : 'none' }}
              colorVariant="blue"
              startIcon={<AddIcon />}
            >
              Nuovo Contatto
            </DefaultContainedButton>
          )}

          {apiCalled && errorMessage?.length === 0 && (
            <DefaultContainedButton
              onClick={() => {
                setNewUserMode('user');
                handleOpenNewUser();
              }}
              customStyles={{ display: canCreateUser ? 'flex' : 'none' }}
              colorVariant="red"
              startIcon={<AddIcon />}
            >
              Nuovo Utente
            </DefaultContainedButton>
          )}

          {/* New User Dialog */}
          <NewUserDialog
            openNewUser={openNewUser}
            setOpenNewUser={setOpenNewUser}
            getUsers={getUsers}
            mode={newUserMode}
          />
        </Stack>
        {apiCalled ? (
          errorMessage?.length === 0 ? (
            <>
              <Card sx={{ padding: '24px', marginBottom: '12px' }}>
                {/* Toggle ricerca manuale / filtri salvati */}
                {/* <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <DefaultContainedButton
                    onClick={() => handleSearchModeChange('manual')}
                    startIcon={<SearchIcon />}
                    customStyles={{
                      ...(searchMode !== 'manual' && {
                        backgroundColor: '#f4f6f8',
                        color: '#637381',
                        '&:hover': { backgroundColor: '#e0e3e7' },
                      }),
                    }}
                  >
                    Ricerca manuale
                  </DefaultContainedButton>
                  <DefaultContainedButton
                    onClick={() => handleSearchModeChange('filter')}
                    startIcon={<FilterListIcon />}
                    customStyles={{
                      ...(searchMode !== 'filter' && {
                        backgroundColor: '#f4f6f8',
                        color: '#637381',
                        '&:hover': { backgroundColor: '#e0e3e7' },
                      }),
                    }}
                  >
                    Filtri salvati
                  </DefaultContainedButton>
                </Stack> */}

                {searchMode === 'manual' ? (
                  <form onSubmit={submitForm}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <CustomTextField
                          id="name"
                          label="Nome"
                          value={formState.name}
                          onChange={(e) => handleFieldChange('name', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <CustomTextField
                          id="surname"
                          label="Cognome"
                          value={formState.surname}
                          onChange={(e) => handleFieldChange('surname', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <CustomTextField
                          id="fiscal_code"
                          label="Codice Fiscale"
                          value={formState.fiscal_code}
                          onChange={(e) => handleFieldChange('fiscal_code', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <CustomTextField
                          id="email"
                          label="Email"
                          value={formState.email}
                          onChange={(e) => handleFieldChange('email', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <CustomTextField
                          id="phone"
                          label="Telefono"
                          value={formState.phone}
                          onChange={(e) => handleFieldChange('phone', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <CustomTextField
                          id="card_number"
                          label="Numero Tessera"
                          value={formState.card_number}
                          onChange={(e) => handleFieldChange('card_number', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} sx={{ alignSelf: 'start' }}>
                        <CustomTextField
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                {formState.entity ? (
                                  <Tooltip title="Elimina" placement="top" arrow>
                                    <DeleteIcon
                                      onClick={() => {
                                        handleEntityChange(null);
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Modifica" placement="top" arrow>
                                    <EditIcon
                                      onClick={() => {
                                        setOpenEntitiesFilter(!openEntitiesFilter);
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </InputAdornment>
                            ),
                          }}
                          value={formState.entity ? formState.entity?.name : ''}
                          label="Entità"
                        />

                        <EntitiesDialog
                          openDialog={openEntitiesFilter}
                          closeDialog={handleCloseEntitiesFilterDialog}
                          outputEntity={handleEntityChange}
                          context={'users'}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <CustomTextField
                          id="category"
                          label="Categoria"
                          select
                          value={formState.category}
                          onChange={(e) => handleFieldChange('category', e.target.value)}
                        >
                          <MenuItem value="">Nessuna Categoria</MenuItem>
                          <MenuItem value="member">Tesserati</MenuItem>
                          <MenuItem value="expired">Da rinnovare</MenuItem>
                          <MenuItem value="verified">Verificati</MenuItem>
                          <MenuItem value="unverified">Non Verificati</MenuItem>
                          <MenuItem value="internal">Con incarico politico</MenuItem>
                          <MenuItem value="external">Con incarico istituzionale</MenuItem>
                          <MenuItem value="online">Online</MenuItem>
                          <MenuItem value="offline">Offline</MenuItem>
                          <MenuItem value="blocked">Bloccati</MenuItem>
                        </CustomTextField>
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <DefaultOutlinedButton
                          onClick={handleResetFilters}
                          customStyles={{ width: '100%', height: '100%', backgroundColor: '#f4f6f8' }}
                        >
                          Svuota Filtri
                        </DefaultOutlinedButton>
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <DefaultContainedButton type="submit" customStyles={{ width: '100%', height: '100%' }}>
                          Cerca
                        </DefaultContainedButton>
                      </Grid>
                    </Grid>
                  </form>
                ) : (
                  <Stack sx={{ paddingTop: '12px' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={8}>
                        <Autocomplete
                          options={savedFilters}
                          getOptionLabel={(option) =>
                            `${option.name} — ${option.entity?.name || ''} (${
                              option.conditions?.length || 0
                            } condizioni)`
                          }
                          value={selectedFilter}
                          onChange={(_, newValue) => setSelectedFilter(newValue)}
                          inputValue={filterSearchText}
                          onInputChange={(_, newInputValue) => {
                            setFilterSearchText(newInputValue);
                            searchFilters(newInputValue);
                          }}
                          loading={filtersLoading}
                          noOptionsText={
                            filterSearchText.length < 3 ? 'Digita almeno 3 caratteri' : 'Nessun filtro trovato'
                          }
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          renderInput={(params) => (
                            <CustomTextField
                              {...params}
                              label="Seleziona un filtro"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {filtersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={3} md={2}>
                        <DefaultOutlinedButton
                          onClick={() => {
                            setSelectedFilter(null);
                            setFilterSearchText('');
                            setSavedFilters([]);
                            setPage(0);
                            sessionStorage.setItem('page', JSON.stringify(0));
                            getUsers('');
                          }}
                          disabled={!selectedFilter}
                          customStyles={{ width: '100%', height: '100%', backgroundColor: '#f4f6f8' }}
                        >
                          Svuota
                        </DefaultOutlinedButton>
                      </Grid>

                      <Grid item xs={12} sm={3} md={2}>
                        <DefaultContainedButton
                          onClick={() => {
                            setPage(0);
                            sessionStorage.setItem('page', JSON.stringify(0));
                            getUsers();
                          }}
                          disabled={!selectedFilter}
                          customStyles={{ width: '100%', height: '100%' }}
                        >
                          Applica
                        </DefaultContainedButton>
                      </Grid>
                    </Grid>
                  </Stack>
                )}
              </Card>

              <Card sx={{ padding: '24px' }}>
                <Dialog open={openToggleVerified} onClose={() => {}} fullWidth>
                  <DialogTitle>Verifica Utente</DialogTitle>
                  <DialogContent>
                    {userToToggleVerifiedInfo.isVerified
                      ? `Sei sicuro di voler rimuovere lo stato di utente verificato a ${userToToggleVerifiedInfo.user.name} ${userToToggleVerifiedInfo.user.surname}?`
                      : `Sei sicuro di voler verificare l'utente ${userToToggleVerifiedInfo.user.name} ${userToToggleVerifiedInfo.user.surname}?`}
                  </DialogContent>
                  <DialogActions>
                    <DefaultOutlinedButton onClick={closeToggleVerifiedDialog}>Chiudi</DefaultOutlinedButton>
                    <DefaultContainedButton onClick={handleToggleVerified}>Conferma</DefaultContainedButton>
                  </DialogActions>
                </Dialog>

                {/* Table */}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome Utente</TableCell>
                        <TableCell>Codice Fiscale</TableCell>
                        {is400Screen && (
                          <>
                            <TableCell>Data di Nascita</TableCell>
                            <TableCell>Circolo</TableCell>
                          </>
                        )}

                        {is600Screen && (
                          <>
                            <TableCell align="center">Verificato</TableCell>
                            <TableCell align="center">Tesserato</TableCell>
                            <TableCell align="center">Online</TableCell>
                            <TableCell align="center">Attivo</TableCell>
                            <TableCell align="center">Fase</TableCell>
                          </>
                        )}

                        <TableCell />
                      </TableRow>
                    </TableHead>

                    {users?.length > 0 ? (
                      <TableBody>
                        {rowsApiCalled ? (
                          users.slice(sliceStartDisplay, sliceEndDisplay).map((user) => {
                            return (
                              <TableRow hover key={user.id}>
                                <TableCell>
                                  {user.name_alias
                                    ? `${user.name_alias} [${user.name} ${user.surname}]`
                                    : `${user.name || ''} ${user.surname || ''}`}
                                </TableCell>
                                <TableCell>{user.fiscal_code}</TableCell>
                                {is400Screen && (
                                  <>
                                    <TableCell>
                                      {user.birth_date ? dayjs(user.birth_date).format('DD/MM/YYYY') : ''}
                                    </TableCell>
                                    <TableCell>{user?.master_entity[0]?.name ?? ''}</TableCell>
                                  </>
                                )}

                                {is600Screen && (
                                  <>
                                    <TableCell align="center">
                                      {user.verified ? (
                                        canVerifyUsers ? (
                                          <Box>
                                            <Tooltip title="Clicca per rimuovere verifica" arrow placement="right">
                                              <Box>
                                                <CustomIconButton onClick={() => openToggleVerifiedDialog(user, true)}>
                                                  <GppGoodIcon sx={{ color: '#47ba25' }} />
                                                </CustomIconButton>
                                              </Box>
                                            </Tooltip>
                                          </Box>
                                        ) : (
                                          <GppGoodIcon sx={{ color: '#47ba25' }} />
                                        )
                                      ) : canVerifyUsers ? (
                                        <Box>
                                          <Tooltip title="Clicca per verificare" arrow placement="right">
                                            <Box>
                                              <CustomIconButton onClick={() => openToggleVerifiedDialog(user, false)}>
                                                <GppBadIcon sx={{ color: 'red' }} />
                                              </CustomIconButton>
                                            </Box>
                                          </Tooltip>
                                        </Box>
                                      ) : (
                                        <GppBadIcon sx={{ color: 'red' }} />
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <MembershipCardIcon data={user} />
                                    </TableCell>
                                    <TableCell align="center">
                                      {user.user_online ? (
                                        <PublicIcon sx={{ color: '#47ba25' }} />
                                      ) : (
                                        <PublicIcon sx={{ color: '#9fa1a2' }} />
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {user.user_status ? (
                                        <Lock sx={{ color: 'red' }} />
                                      ) : (
                                        <LockOpenIcon sx={{ color: '#47ba25' }} />
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <CommonLabel
                                        textColor="white"
                                        bgColor={
                                          user.pipeline_stage === 'contact'
                                            ? '#1a73e8'
                                            : user.pipeline_stage === 'member'
                                            ? 'green'
                                            : 'red'
                                        }
                                        text={
                                          user.pipeline_stage === 'contact'
                                            ? 'Contatto'
                                            : user.pipeline_stage === 'member'
                                            ? 'Membro'
                                            : 'Utente'
                                        }
                                      />
                                    </TableCell>
                                  </>
                                )}
                                <TableCell>
                                  <CustomIconButton
                                    onClick={() => {
                                      navigate('/admin/users/user-details', { state: { userID: user.id } });
                                    }}
                                  >
                                    <KeyboardArrowRightIcon />
                                  </CustomIconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={9}>{LoadingUI()}</TableCell>
                          </TableRow>
                        )}

                        {/* Empty Rows */}
                        {emptyRows > 0 && apiCalled && (
                          <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={9} />
                          </TableRow>
                        )}
                      </TableBody>
                    ) : (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ textAlign: 'center' }}>
                            Nessun risultato disponibile
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}

                    {/* Table Footer */}
                    {apiCalled && (
                      <TableFooter>
                        <TableRow>
                          <ThemeProvider theme={theme}>
                            <TablePagination
                              sx={{ border: 0 }}
                              rowsPerPageOptions={[]}
                              count={apiResponseData?.total}
                              rowsPerPage={rowsPerPage}
                              page={page}
                              onPageChange={handleChangePage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              ActionsComponent={(props) => (
                                <PaginationAPI
                                  {...props}
                                  elements={10}
                                  apiResponseData={apiResponseData}
                                  setApiResponseData={setApiResponseData}
                                  lastPage={apiResponseData.last_page}
                                />
                              )}
                            />
                          </ThemeProvider>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              </Card>
            </>
          ) : (
            // Show error message
            ErrorUI(errorMessage)
          )
        ) : (
          // Show loading
          LoadingUI()
        )}
      </Container>
    </>
  );
}
